import React, { useRef } from 'react';
import './CircleChart.scss';
import InnerCircle from './InnerCircle';

const size = 224;
const stroke = 24;

interface CircleChartProps {
  current: number;
  remaining: number;
  required: number;
  retired?: boolean;
}

// eslint-disable-next-line react/prop-types
export default function CircleChart({
  current,
  remaining,
  required,
  retired = false,
}: CircleChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  let data: any = [
    { tag: 'Redeemed', value: current, color: '#57c4e5' },
    { tag: 'Available', value: remaining, color: 'transparent' },
  ];

  data = data.map((item: { value: any }, index: React.Key, array: any[]) => {
    const { value } = item;
    const countTotal = array.reduce(
      (a: any, b: { value: any }) => a + b.value,
      0,
    );
    const percentage = (value / countTotal) * 100;
    const remainder = 100 - percentage;
    return {
      ...item,
      percentage,
      remainder,
    };
  });

  data = data.map((item: any, index: number) => {
    const startAngle = index === 0 ? 0 : data[index - 1].percentage * 3.6;
    return {
      ...item,
      startAngle,
      endAngle: startAngle + data[index].percentage * 3.6,
    };
  });

  data = data.map((item: any, index: number) => {
    const startAngle = index === 0 ? 0 : data[index - 1].endAngle;
    return {
      ...item,
      startAngle,
      endAngle: startAngle + data[index].percentage * 3.6,
    };
  });

  const radius = size / 2 - stroke / 2;
  const centerCoordinate = size / 2;
  const circumference = Math.PI * (radius * 2);
  const circlePath = `
    M ${centerCoordinate}, ${centerCoordinate}
    m -${radius}, 0
    a ${radius},${radius} 0 1,0 ${radius * 2},0
    a ${radius},${radius} 0 1,0 -${radius * 2},0
  `;

  const value = current / required;
  const valuePercent = `${value * 100}%`;

  const renderCurrentValue = (oldValue: number) => {
    if (!Number.isInteger(oldValue)) {
      const regexp = /(.*)\.(.+)/;
      const convertedValue = oldValue.toFixed(2).toString();
      const integerValue = convertedValue.match(regexp)[1];
      const supValue = convertedValue.match(regexp)[2];

      return (
        <>
          {integerValue}
          <sup>{`.${supValue}`}</sup>
        </>
      );
    }

    return oldValue;
  };

  const halfCircle = circumference / 2;
  const offsetDouble = halfCircle * value;
  const strokeDasharrayDouble = `${offsetDouble} ${
    circumference - offsetDouble
  }`;

  const offsetSingle = circumference * value;
  const strokeDasharraySingle = `${offsetSingle} ${
    circumference - offsetSingle
  }`;
  const strokeDashoffsetSingle = offsetSingle - halfCircle;

  const isDoublePaths = value > 0.5;

  // handle dynamic position of fractions inside donut chart
  let fontSize = '';
  let separatorSize = '';
  let separatorLeftPosition = '';
  let separatorTopPosition = '';

  if (required.toString().length === 4) {
    fontSize = '18px';
    separatorLeftPosition = '26px';
    separatorSize = '36px';
    separatorTopPosition = '34px';
  }

  if (required.toString().length === 5) {
    fontSize = '17px';
    separatorSize = '32px';
    separatorLeftPosition = '28px';
    separatorTopPosition = '32px';
  }

  if (required.toString().length === 6) {
    fontSize = '15px';
    separatorSize = '31px';
    separatorLeftPosition = '32px';
    separatorTopPosition = '36px';
    if (current.toString().length < required.toString().length) {
      separatorTopPosition = '32px';
    }
  }

  if (required.toString().length === 7) {
    fontSize = '14px';
    separatorSize = '30px';
    separatorLeftPosition = '28px';
    separatorTopPosition = '36px';
    if (current.toString().length < required.toString().length) {
      separatorTopPosition = '34px';
    }
  }
  if (required.toString().length >= 8) {
    fontSize = '13px';
    separatorSize = '30px';
    separatorLeftPosition = '24px';
    separatorTopPosition = '36px';
    if (current.toString().length < required.toString().length) {
      separatorTopPosition = '34px';
    }
  }

  if (
    required.toString().length >= 4 &&
    current.toString().length < required.toString().length
  ) {
    separatorLeftPosition = '24px';
  }

  return (
    <div
      ref={chartRef}
      className="pmi-circle-chart"
      style={{ height: `${size}px`, width: `${size}px` }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="pmi-circle-chart__bar"
      >
        <linearGradient id="linearColors-1" x1="100%" x2="0%">
          <stop offset="0%" stopColor="#0080A8" />
          {isDoublePaths ? (
            <stop offset={valuePercent} stopColor="#59D4EB" />
          ) : (
            <stop offset="100%" stopColor="#59D4EB" />
          )}
        </linearGradient>
        {isDoublePaths && (
          <linearGradient id="linearColors-2-1" x1="100%" x2="0%">
            <stop offset="0%" stopColor="#59D4EB" />
            <stop offset={valuePercent} stopColor="#59D4EB" />
          </linearGradient>
        )}
        <circle
          cx={centerCoordinate}
          cy={centerCoordinate}
          fill="none"
          r={radius}
          stroke="#EAECF0"
          strokeLinecap="round"
          strokeWidth={stroke}
        />
        {current > 0 && (
          <>
            <path
              d={circlePath}
              fill="none"
              stroke="url(#linearColors-1)"
              strokeDasharray={
                isDoublePaths ? strokeDasharrayDouble : strokeDasharraySingle
              }
              strokeDashoffset={
                isDoublePaths
                  ? offsetDouble - halfCircle
                  : strokeDashoffsetSingle
              }
              strokeLinecap="round"
              strokeWidth={stroke}
            />
            {isDoublePaths && (
              <path
                id="path-double-path-id"
                d={circlePath}
                fill="none"
                stroke="url(#linearColors-2-1)"
                strokeDasharray={strokeDasharrayDouble}
                strokeDashoffset={offsetDouble * 2 - halfCircle}
                strokeLinecap="round"
                strokeWidth={stroke}
              />
            )}
          </>
        )}

        {data.map((tag: { tag: string }) => (
          <InnerCircle
            key={tag.tag}
            centerX={centerCoordinate}
            centerY={centerCoordinate}
            radius={radius}
            data={tag}
            container={chartRef}
          />
        ))}
      </svg>
      <div className="pmi-circle-chart__content">
        <div className="pmi-fractions">
          <span className="pmi-fractions__current">
            <span
              className="pmi-fractions__current__content"
              style={{
                fontSize,
              }}
            >
              {renderCurrentValue(current)}
            </span>
          </span>
          <span
            className="pmi-fractions__separator"
            style={{
              width: separatorSize,
              left: separatorLeftPosition,
              top: separatorTopPosition,
            }}
          />
          <span className="pmi-fractions__total" style={{ fontSize }}>
            {required}
          </span>
        </div>
        {!retired && (
          <div className="pmi-pdu-earned">
            <span className="earned-text">
              {remaining > 0 ? remaining : '0'} Remaining
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export const withMemoCertificationCircleChart = React.memo(CircleChart);
